import { queryKeys } from '@/lib/query-keys';
import { dashboardClient } from '@/lib/api/ts-rest';
import type {
  DashboardBillingSummary,
  DashboardSimStock,
  DashboardSummary,
} from '@octave/contracts';
import { useQuery } from '@tanstack/react-query';
import { useDefaultSite } from './useDefaultSite';
import { useCurrentOrganization } from './useOrganization';

type Owner =
  | { id: string; type: 'organization' | 'site' }
  | null;

const STALE_TIME_MS = 5 * 60 * 1000;

const useOwner = (): Owner => {
  const { data: organization } = useCurrentOrganization();
  const { currentSite } = useDefaultSite();
  if (currentSite) return { id: currentSite.id, type: 'site' };
  if (organization) return { id: organization.id, type: 'organization' };
  return null;
};

export const useDashboardSummary = () => {
  const owner = useOwner();

  return useQuery<DashboardSummary, Error>({
    queryKey: queryKeys.dashboard.summary(owner),
    queryFn: async () => {
      if (!owner) throw new Error('No owner selected');
      const res = await dashboardClient.getDashboardSummary({
        query: { ownerId: owner.id, ownerType: owner.type },
      });
      if (res.status !== 200) throw new Error('Failed to load dashboard');
      return res.body;
    },
    enabled: !!owner,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: STALE_TIME_MS,
  });
};

export const useDashboardSimStock = () => {
  const owner = useOwner();

  return useQuery<DashboardSimStock, Error>({
    queryKey: queryKeys.dashboard.simStock(owner),
    queryFn: async () => {
      if (!owner) throw new Error('No owner selected');
      const res = await dashboardClient.getDashboardSimStock({
        query: { ownerId: owner.id, ownerType: owner.type },
      });
      if (res.status !== 200) throw new Error('Failed to load SIM stock');
      return res.body;
    },
    enabled: !!owner,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: STALE_TIME_MS,
  });
};

export const useDashboardBilling = () => {
  const owner = useOwner();

  return useQuery<DashboardBillingSummary, Error>({
    queryKey: queryKeys.dashboard.billing(owner),
    queryFn: async () => {
      if (!owner) throw new Error('No owner selected');
      const res = await dashboardClient.getDashboardBilling({
        query: { ownerId: owner.id, ownerType: owner.type },
      });
      if (res.status !== 200) throw new Error('Failed to load billing');
      return res.body;
    },
    enabled: !!owner,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: STALE_TIME_MS,
  });
};
